import * as React from 'react';
import * as classes from '@common/components/wizard/_components/style.scss';
import { RouteComponentProps, Redirect } from 'react-router';
import { RouteDefinitions } from '@common/route-definitions';
import { history } from '../../../..';
import { getRoute } from '../../../route-definitions';

interface WizardStep {
  name: string;
  // tslint:disable-next-line:no-any
  component: React.ComponentType<RouteComponentProps<any>> | React.ComponentType<any>;
  canSkip?: boolean;
  readonly next?: Function;
  readonly prev?: Function;
}

export interface WizardProps {
  readonly steps: WizardStep[];
  readonly isQuoteOrContractFlow?: boolean;
  readonly isRenewal?: boolean;
  readonly renewalAsIs?: boolean;
  readonly isQuote?: boolean;
  readonly cancelRoute?: string;
  readonly cancelRouteId?: string;
  readonly redirectTo?: string;
  readonly createRedirectTo?: RedirectProps;
}

// NOTE: THIS IS USED IN SITUATIONS WHEN THE WIZARD NEEDS TO REDIRECT TO A NEWLY CREATED ITEM LIKE A QUOTE ONCE AN ID IS IN THE STORE
interface RedirectProps {
  routeDef: string;
  getId: () => number | string;
}

export interface WizardState {
  currentStep: number;
  completedSteps: boolean[];
  styles: string[];
}

export class Wizard extends React.Component<WizardProps, WizardState> {
  private button: HTMLButtonElement;

  constructor(props: WizardProps) {
    super(props);
    this.state = {
      currentStep: 0,
      completedSteps: this.props.steps.map(step => false),
      styles: this.getStyles(0, this.props.steps.length)
    };
  }

  jump = (step: number) => {
    if (step > this.state.currentStep! && !this.state.completedSteps[step - 1]) {
      return;
    }
    this.setState({
      currentStep: step,
      styles: this.getStyles(step, this.props.steps.length)
    });
  };

  getStyles(index: number, length: number) {
    let styles: string[] = [];

    for (let i = 0; i < length; i++) {
      if (i < index) {
        styles.push('done');
      } else if (i === index) {
        styles.push('doing');
      } else {
        styles.push('todo');
      }
    }

    return styles;
  }

  getStepClass(className: string, index: number) {
    let styleClassName: string = className + '-' + this.state.styles[index];

    if (index === 0) {
      styleClassName = styleClassName + ' ' + className + '-first';
    }

    return styleClassName;
  }

  stepDisplayNumber(stepNumber: number, size?: number) {
    let s: string = stepNumber.toString();
    // while (s.length < (size || 2)) { s = s; }
    return s;
  }

  validate() {
    if (this.button) {
      this.button.click();
    } else {
      this.next();
    }
  }

  next() {
    // if (this.props.steps.length === this.state.currentStep! + 1) {
    //     return;
    // }

    this.setState(prevState => {
      return {
        currentStep: prevState.currentStep! + 1,
        completedSteps: prevState.completedSteps.map((step, index) => step || prevState.currentStep! === index),
        styles: this.getStyles(prevState.currentStep! + 1, this.props.steps.length)
      };
    });

    document.getElementsByTagName('main')[0].scrollTo(0, 0);
  }

  prev() {
    this.setState(prevState => {
      return {
        currentStep: prevState.currentStep! - 1,
        styles: this.getStyles(prevState.currentStep! - 1, this.props.steps.length)
      };
    });

    document.getElementsByTagName('main')[0].scrollTo(0, 0);
  }

  renderSteps() {
    return this.props.steps.map((step, index) => (
      <li
        style={{
          width:
            index === this.props.steps.length - 1 ? 'auto' : (100 / this.props.steps.length - 3.5).toString() + 'vw'
        }}
        className={this.getStepClass('rail-step', index)}
        key={index}
        onClick={() => {
          this.railStepHelper(index);
        }}
      >
        <div>{this.stepDisplayNumber(index + 1)}</div>
        <span className={classes.stepName + ' ellipsis'}>{step.name}</span>
      </li>
    ));
  }

  railStepHelper(index: number) {
    // if we're on the last step of the quote creation, the quote has already been created and we shouldn't be able to go back to the previous steps.
    if (this.props.isQuoteOrContractFlow && !(this.state.currentStep !== this.props.steps.length - 1)) {
      return;
    }

    return this.jump(index);
  }

  showPrevHelper(showNext: boolean) {
    if (this.state.currentStep === 0 || (this.props.isQuoteOrContractFlow && !showNext)) {
      // if we're on the last step of quote creation, we should remove the previous button.
      return false;
    }
    return true;
  }

  render() {
    let cloneExtensions = {
      buttonRef: (button: HTMLButtonElement) => {
        this.button = button;
      },
      next: this.next.bind(this),
      jump: this.jump,
      currentStep: this.state.currentStep,
      isRenewal: this.props.isRenewal!,
      renewalAsIs: this.props.renewalAsIs!,
      ...this.props
    };

    const finish: boolean = this.state.completedSteps.filter(step => step).length === this.props.steps.length;
    let comp;

    if (!finish) {
      comp = React.createElement(this.props.steps[this.state.currentStep!].component, cloneExtensions);
    }
    const showNext: boolean = this.state.currentStep !== this.props.steps.length - 1;
    const showPrev: boolean = this.showPrevHelper(showNext);

    const redirect = this.props.redirectTo
      ? this.props.redirectTo
      : this.props.createRedirectTo
        ? getRoute(this.props.createRedirectTo.routeDef, { id: this.props.createRedirectTo.getId() })
        : RouteDefinitions.root;

    if (finish) {
      return <Redirect to={redirect} />;
    }

    return (
      <div className={classes.wizard}>
        <div className="row center-xs middle-xs">
          <div className="col-xs-12">
            <ol className={classes.rails}>{this.renderSteps()}</ol>
          </div>
        </div>
        <div className={classes.body}>{comp}</div>
        <div className={classes.footer}>
          <div className="row between-xs">
            <button
              className="btn btn-primary"
              onClick={() => {
                history.push(
                  this.props.cancelRouteId && this.props.cancelRoute
                    ? getRoute(this.props.cancelRoute, JSON.parse(this.props.cancelRouteId))
                    : this.props.cancelRoute
                      ? this.props.cancelRoute
                      : history.goBack()
                );
              }}
            >
              Cancel
            </button>
            <button
              disabled={!showPrev}
              className="btn btn-primary"
              onClick={() => {
                this.prev();
              }}
            >
              Previous
            </button>
            <div>
              {this.props.steps[this.state.currentStep].canSkip ? (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    this.next();
                  }}
                >
                  Skip
                </button>
              ) : (
                ''
              )}
              <button
                id="wizardNextButton"
                className="btn btn-primary"
                onClick={() => {
                  this.validate();
                }}
              >
                {showNext ? 'Next' : 'Complete'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/common/components/wizard/_components/wizard.component.tsx